import { useSuspenseQuery } from "@tanstack/react-query";
import type { FC, ReactNode } from "react";
import { Suspense, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
	type MetaAdsCampaignDraft,
	type MetaAdsCampaignTemplate,
	postLabel,
} from "shared";
import { Api } from "../api.config";
import { useCompany } from "../company/useCompany";
import { Stepper } from "../components/Stepper";
import { LinkGen, QueryKeys } from "../LinkGen";
import { AppPage } from "../layout/AppLayout";
import {
	CampaignWizardProvider,
	useCampaignWizard,
} from "./CampaignWizardContext";
import { useRegisterMetaAdsUnsavedWork } from "./metaAdsUnsavedWork";
import { StepAdSet } from "./StepAdSet";
import { StepCampaign } from "./StepCampaign";
import { StepCreative } from "./StepCreative";
import { StepLeadForm } from "./StepLeadForm";
import { StepReview } from "./StepReview";
import { useCanEditMetaAds } from "./useMyMetaAdsRole";

const STEP_COMPONENTS: Record<string, FC> = {
	Campaign: StepCampaign,
	"Ad Set": StepAdSet,
	"Lead Form": StepLeadForm,
	Creative: StepCreative,
	Review: StepReview,
};

const WizardContent: FC = () => {
	const { currentStep, steps, isDirty } = useCampaignWizard();
	const StepComponent = STEP_COMPONENTS[steps[currentStep].title];

	useRegisterMetaAdsUnsavedWork(isDirty, "Unsaved campaign wizard changes");

	useEffect(() => {
		const handler = (event: BeforeUnloadEvent) => {
			if (isDirty) {
				event.preventDefault();
			}
		};
		window.addEventListener("beforeunload", handler);
		return () => window.removeEventListener("beforeunload", handler);
	}, [isDirty]);

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
			<Stepper steps={steps} current={currentStep} />
			{StepComponent && <StepComponent />}
		</div>
	);
};

function templateToOverrides(
	template: MetaAdsCampaignTemplate,
): Partial<MetaAdsCampaignDraft> {
	return {
		objective: template.objective,
		...template.settings,
	};
}

const CampaignEditPage: FC<{ campaignId: string }> = ({ campaignId }) => {
	const company = useCompany();
	const { data: campaignDraft } = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.CampaignForEdit(
			company.id,
			campaignId,
		),
		queryFn: () =>
			Api.GetMetaAdsCampaignForEdit(campaignId).then((r) => r.data),
	});

	return (
		<AppPage title="Edit Campaign">
			<CampaignWizardProvider
				initialOverrides={campaignDraft}
				companyName={company.name}
			>
				<WizardContent />
			</CampaignWizardProvider>
		</AppPage>
	);
};

const CampaignDraftEditPage: FC<{ draftId: string }> = ({ draftId }) => {
	const company = useCompany();
	const { data: draft } = useSuspenseQuery({
		queryKey: QueryKeys.Integrations.MetaAds.Draft(company.id, draftId),
		queryFn: () => Api.GetMetaAdsDraft(draftId).then((r) => r.data),
	});

	return (
		<AppPage title="Edit Campaign Draft">
			<CampaignWizardProvider
				initialOverrides={draft}
				companyName={company.name}
			>
				<WizardContent />
			</CampaignWizardProvider>
		</AppPage>
	);
};

const EditorGate: FC<{ title: string; children: ReactNode }> = ({
	title,
	children,
}) => {
	const canEdit = useCanEditMetaAds();
	const navigate = useNavigate();

	useEffect(() => {
		if (!canEdit) {
			navigate(LinkGen.MetaAds.Index(), { replace: true });
		}
	}, [canEdit, navigate]);

	if (!canEdit) {
		return (
			<AppPage title={title}>
				<p className="text-muted">
					Editor access is required to create or edit campaigns.
				</p>
			</AppPage>
		);
	}

	return children;
};

const CampaignEditLoadError: FC = () => (
	<AppPage title="Edit Campaign">
		<p className="text-muted">
			Unable to load this campaign for editing. It may have been created outside
			Moxie, or Meta may be temporarily unavailable.
		</p>
		<p style={{ marginBottom: 0 }}>
			<Link to={LinkGen.MetaAds.Reporting()}>Back to Reporting</Link>
		</p>
	</AppPage>
);

export const PageCampaignCreate: FC = () => {
	const company = useCompany();
	const location = useLocation();
	const { campaignId, draftId } = useParams<{
		campaignId?: string;
		draftId?: string;
	}>();
	const fromPost = location.state?.fromPost;
	const fromTemplate: MetaAdsCampaignTemplate | undefined =
		location.state?.fromTemplate;

	if (draftId) {
		return (
			<Suspense
				fallback={
					<AppPage title="Edit Campaign Draft">
						<p>Loading draft...</p>
					</AppPage>
				}
			>
				<EditorGate title="Edit Campaign Draft">
					<CampaignDraftEditPage draftId={draftId} />
				</EditorGate>
			</Suspense>
		);
	}

	if (campaignId) {
		return (
			<ErrorBoundary fallback={<CampaignEditLoadError />}>
				<Suspense
					fallback={
						<AppPage title="Edit Campaign">
							<p>Loading campaign...</p>
						</AppPage>
					}
				>
					<EditorGate title="Edit Campaign">
						<CampaignEditPage campaignId={campaignId} />
					</EditorGate>
				</Suspense>
			</ErrorBoundary>
		);
	}

	let initialOverrides: Partial<MetaAdsCampaignDraft> | undefined;
	if (fromTemplate) {
		initialOverrides = templateToOverrides(fromTemplate);
	} else if (fromPost) {
		initialOverrides = {
			name: postLabel(fromPost),
			primaryText: fromPost.content,
		};
	}

	return (
		<Suspense
			fallback={
				<AppPage title="Create Campaign">
					<p>Loading...</p>
				</AppPage>
			}
		>
			<EditorGate title="Create Campaign">
				<AppPage title="Create Campaign">
					<CampaignWizardProvider
						initialOverrides={initialOverrides}
						companyName={company.name}
					>
						<WizardContent />
					</CampaignWizardProvider>
				</AppPage>
			</EditorGate>
		</Suspense>
	);
};
